import React, { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../../../contexts/CompanyContext';
import { ir13API } from '../../../api/ir13';
import { exportIR13CSV, exportIR13TXT } from '../../../utils/ir13Export';
import ListadoIR13   from './components/ListadoIR13';
import DetalleIR13   from './components/DetalleIR13';
import CrearIR13Modal from './components/CrearIR13Modal';

export default function IR13Module() {
  const { currentCompany } = useCompany();

  const [ir13List,       setIr13List]       = useState([]);
  const [selectedIR13,   setSelectedIR13]   = useState(null);
  const [detalles,       setDetalles]       = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [loadingDetalles, setLoadingDetalles] = useState(false);
  const [generating,     setGenerating]     = useState(false);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [activeTab,      setActiveTab]      = useState('resumen');
  const [error,          setError]          = useState(null);

  // ── Cargar lista de IR-13 ──────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await ir13API.getAll(currentCompany.id);
      setIr13List(data.ir13s || []);
    } catch (err) {
      setError('No se pudo cargar la lista de declaraciones IR-13.');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Cargar detalles cuando se selecciona un IR-13 ─────────────────────────
  useEffect(() => {
    if (!selectedIR13) { setDetalles([]); return; }
    setLoadingDetalles(true);
    ir13API.getDetalles(selectedIR13.id)
      .then(data => setDetalles(data.detalles || []))
      .catch(() => setDetalles([]))
      .finally(() => setLoadingDetalles(false));
  }, [selectedIR13]);

  // ── Generar IR-13 ─────────────────────────────────────────────────────────
  const handleGenerar = async (anio, incluirInactivos) => {
    setGenerating(true);
    setError(null);
    try {
      const data = await ir13API.generar(currentCompany.id, anio, incluirInactivos);
      setShowCrearModal(false);
      await loadList();
      // Seleccionar el recién generado
      const nuevo = data.ir13;
      setSelectedIR13(nuevo);
      setActiveTab('resumen');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Error al generar el IR-13. Intenta de nuevo.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // ── Cerrar IR-13 ──────────────────────────────────────────────────────────
  const handleCerrar = async () => {
    const errores = detalles.filter(d => !d.tiene_datos).length;
    let confirmMsg = `¿Confirmas el cierre del IR-13 del año ${selectedIR13.anio}?\n\nEsta acción es irreversible.`;
    if (errores > 0) {
      confirmMsg = `Hay ${errores} empleado(s) sin registros de nómina.\n\n${confirmMsg}`;
    }
    if (!window.confirm(confirmMsg)) return;

    try {
      const result = await ir13API.cerrar(selectedIR13.id);
      if (result.advertencias?.length > 0) {
        alert('IR-13 cerrado con advertencias:\n• ' + result.advertencias.join('\n• '));
      }
      const updatedIR13 = result.ir13;
      setSelectedIR13(updatedIR13);
      setIr13List(prev => prev.map(r => r.id === updatedIR13.id ? updatedIR13 : r));
    } catch (err) {
      alert('Error al cerrar el IR-13: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Eliminar IR-13 ────────────────────────────────────────────────────────
  const handleEliminar = async () => {
    if (!window.confirm(`¿Eliminar el IR-13 borrador del año ${selectedIR13.anio}? Esta acción no puede deshacerse.`)) return;
    try {
      await ir13API.eliminar(selectedIR13.id);
      setSelectedIR13(null);
      setDetalles([]);
      await loadList();
    } catch (err) {
      alert('Error al eliminar: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Exports ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => exportIR13CSV(selectedIR13, detalles);
  const handleExportTXT = () => exportIR13TXT(selectedIR13, detalles);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* Error global */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* Vista principal */}
      {selectedIR13 ? (
        <DetalleIR13
          ir13={selectedIR13}
          detalles={detalles}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onBack={() => { setSelectedIR13(null); setDetalles([]); }}
          onCerrar={handleCerrar}
          onEliminar={handleEliminar}
          onExportCSV={handleExportCSV}
          onExportTXT={handleExportTXT}
          loading={loadingDetalles}
        />
      ) : (
        <ListadoIR13
          ir13List={ir13List}
          onSelect={(ir13) => { setSelectedIR13(ir13); setActiveTab('resumen'); }}
          onCrear={() => setShowCrearModal(true)}
          loading={loading}
        />
      )}

      {/* Modal crear */}
      {showCrearModal && (
        <CrearIR13Modal
          onClose={() => { if (!generating) setShowCrearModal(false); }}
          onConfirm={handleGenerar}
          generating={generating}
        />
      )}

    </div>
  );
}
